/**
 * ApprovalView — `/approvals/$artifactId`
 *
 * OT-0013 — pantalla de aprobación artifact-scoped, HERMANA de
 * `projectShellRoute` (no hija): no llama `getProject` ni ningún
 * endpoint `/projects/...`. Existe para que un aprobador de tier
 * `external` pueda abrir su pendiente desde la bandeja sin que el
 * guard de `can_view_project` (que lo niega por diseño — Doc 29 §4.6)
 * le bloquee el paso.
 *
 * Datos — los cuatro endpoints artifact-scoped que el `external` ya
 * alcanza vía `require_artifact_view`:
 *   1. GET /artifacts/{id}                    → metadata + current_version_id
 *   2. GET /artifacts/{id}/template            → plantilla PROYECTADA (sin
 *      instrucciones de IA del tenant — a diferencia del snapshot que
 *      consume el editor)
 *   3. GET /artifacts/{id}/versions/{vid}      → content JSONB
 *   4. (implícito, dentro de ApprovalPanel) approvals de la versión
 *
 * `renderLocale` sale de `version.content_language` — NO de
 * `project.content_language`, que esta vista no tiene forma de leer
 * (Corrección 3, §4.3 del documento de la OT).
 *
 * Miembros y admins siguen yendo a `/projects/$projectId/official/
 * $artifactId` (`HomeView.tsx` decide el destino con `isExternal`) —
 * esta ruta no reemplaza esa; sirve al caso que esa no puede servir.
 */

import { useMemo } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError, isNotFound } from '../../api/client'
import {
  getArtifact,
  getArtifactTemplate,
  getArtifactVersion,
} from '../../api/artifacts'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { ApprovalPanel } from '../../components/projects/ApprovalPanel'
import {
  ArtifactMetadata,
  ArtifactPreview,
} from '../../components/projects/official-viewer/ArtifactPreview'
import { UploadedFileViewer } from '../../components/projects/official-viewer/UploadedFileViewer'
import {
  templateI18nSectionMap,
  type TemplateI18nSection,
} from '../../components/projects/official-viewer/section_types'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { canReadTenantInternalResources } from '../../lib/tiers'
import { NotFoundView } from '../NotFoundView'

export function ApprovalView() {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const { me } = useMe()
  const navigate = useNavigate()
  const { artifactId } = useParams({ from: '/approvals/$artifactId' })

  const artifactQuery = useQuery({
    queryKey: ['artifacts', artifactId],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getArtifact(accessToken, artifactId)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  const templateQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'template'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getArtifactTemplate(accessToken, artifactId)
    },
    enabled: !!accessToken,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
    retry: false,
  })

  const currentVersionId = artifactQuery.data?.current_version_id ?? null
  const versionQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', currentVersionId],
    queryFn: () => {
      if (!accessToken || !currentVersionId)
        throw new Error('no_access_token_or_version')
      return getArtifactVersion(accessToken, artifactId, currentVersionId)
    },
    enabled: !!accessToken && !!currentVersionId,
    staleTime: 60_000,
    retry: false,
  })

  const templateDef = templateQuery.data ?? null
  const version = versionQuery.data ?? null

  // Corrección 3 (§4.3) — sale de `artifact_versions.content_language`,
  // que esta vista ya descarga; NO de `project.content_language` (esta
  // vista no tiene proyecto).
  const renderLocale = useMemo<'es' | 'en'>(() => {
    if (!templateDef?.i18n) return 'es'
    const versionLang = version?.content_language
    if (
      (versionLang === 'es' || versionLang === 'en') &&
      templateDef.i18n[versionLang]
    ) {
      return versionLang
    }
    if (templateDef.i18n.es) return 'es'
    if (templateDef.i18n.en) return 'en'
    return 'es'
  }, [templateDef, version?.content_language])

  const templateI18nMap = useMemo(
    () =>
      templateDef
        ? templateI18nSectionMap(templateDef, renderLocale)
        : new Map<string, TemplateI18nSection>(),
    [templateDef, renderLocale],
  )

  if (
    artifactQuery.isLoading ||
    templateQuery.isLoading ||
    (currentVersionId && versionQuery.isLoading)
  ) {
    return (
      <ScrollPage>
        <p className="font-mono text-xs text-ink-subtle">
          {t('official_viewer.loading')}
        </p>
      </ScrollPage>
    )
  }

  // 404 = no existe, es de otro tenant o el usuario no es su aprobador: el
  // backend no los distingue a propósito, y esta vista tampoco (OT-0017).
  if (
    isNotFound(artifactQuery.error) ||
    isNotFound(templateQuery.error) ||
    isNotFound(versionQuery.error)
  ) {
    return <NotFoundView />
  }

  if (artifactQuery.error) {
    return (
      <ScrollPage>
        <ErrorBox error={artifactQuery.error} />
      </ScrollPage>
    )
  }
  if (templateQuery.error) {
    return (
      <ScrollPage>
        <ErrorBox error={templateQuery.error} />
      </ScrollPage>
    )
  }

  const artifact = artifactQuery.data
  if (!artifact) return null

  const backLink = {
    label: t('official_viewer.back_to_inbox'),
    fallback: () => void navigate({ to: '/' }),
  }

  if (!currentVersionId) {
    return (
      <ScrollPage>
        <div className="max-w-5xl">
          <div className="rounded-md border border-review/40 bg-review-soft/40 p-4 font-body text-sm text-ink-muted">
            {t('official_viewer.errors.no_current_version')}
          </div>
        </div>
      </ScrollPage>
    )
  }

  if (versionQuery.error) {
    return (
      <ScrollPage>
        <ErrorBox error={versionQuery.error} />
      </ScrollPage>
    )
  }
  if (!version || !templateDef) return null

  // Mismo criterio que `ProjectOfficialArtifactView`: el panel es
  // compartido, se muestra siempre que haya (o haya habido) un flujo
  // de aprobación sobre esta versión.
  // OT-0017 — sub-fase, plantilla y `section_id` son identificadores del
  // ciclo de vida y del catálogo, que el `external` no lee y no tienen
  // etiqueta legible aquí. Sin `/me` resuelto tampoco se muestran.
  const showInternalIds = canReadTenantInternalResources(me?.role)

  const showApprovalPanel =
    artifact.status === 'in_review' ||
    artifact.status === 'approved' ||
    artifact.status === 'superseded'

  return (
    <ScrollPage>
      <ArtifactPreview
        artifact={artifact}
        version={version}
        templateDef={templateDef}
        renderLocale={renderLocale}
        mode="view"
        backLink={backLink}
        showSectionIds={showInternalIds}
        bodySlot={
          // Un artefacto `upload` no tiene `content`: el backend prohíbe
          // editarlo para preservar la exclusión mutua contenido↔archivo, así
          // que `version.content` es `{}` y el árbol de secciones se pinta
          // vacío. Sin este slot el aprobador abre su pendiente y no ve nada
          // que aprobar. `canManageFile={false}`: ve y descarga, no reemplaza
          // ni borra — eso pertenece al dueño, en el visor oficial.
          artifact.source_mode === 'upload' ? (
            <UploadedFileViewer
              artifact={artifact}
              version={version}
              canManageFile={false}
            />
          ) : undefined
        }
        banner={
          showApprovalPanel ? (
            <ApprovalPanel
              artifactId={artifact.id}
              versionId={version.id}
              reviewCycle={version.review_cycle}
              isCanon={artifact.is_canon}
              currentUserId={me?.id ?? null}
              locale={renderLocale}
              templateI18nMap={templateI18nMap}
            />
          ) : null
        }
        metadataExtras={
          <ArtifactMetadata
            artifact={artifact}
            versionId={version.id}
            versionNumber={version.version_number}
            versionHash={version.hash}
            versionApprovedAt={version.approved_at}
            versionIndexedAt={version.indexed_at}
            contentLanguage={version.content_language}
            {...(showInternalIds
              ? {
                  subphaseCode: artifact.subphase_code ?? undefined,
                  templateKey: artifact.template_key,
                  templateVersion: artifact.template_version,
                }
              : {})}
          />
        }
      />
    </ScrollPage>
  )
}

// ─── ErrorBox ───────────────────────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
